import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dataService, { type IResponse } from 'services/dataService';
import useApi, { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import { showResult } from 'infrastructure/hooks/utils';
import {
  API_ADMIN_MEDICATIONS,
  apiAdminMedicationPapForms,
  apiPapFormTemplateDownload,
  apiAdminPapFormsConfirm,
  apiAdminPapFormUpdate,
} from 'services/dataService/resources/medications';

import type {
  IAdminMedicationDetails,
  IAdminMedicationListResponse,
  IAdminPapFormsResponse,
  ICreatePapFormPayload,
  IEditPapFormPayload,
  IUpdatePapFormStatusPayload,
} from 'infrastructure/interfaces';

export const ADMIN_MEDICATIONS_PER_PAGE = 30;

export type PapAvailabilityFilterValue = 'all' | 'available' | 'not_available';
export type OtcFilterValue = 'all' | 'yes' | 'no';

export type TAdminMedicationsFilter = {
  page?: number;
  search?: string;
  papAvailability?: PapAvailabilityFilterValue;
  otc?: OtcFilterValue;
  manufacturer?: string;
  sort?: string;
};

const buildQueryParams = (filter: TAdminMedicationsFilter = {}) => {
  const { page = 1, search, papAvailability, otc, manufacturer, sort } = filter;

  return {
    page,
    items: ADMIN_MEDICATIONS_PER_PAGE,
    search: search?.trim() ? search.trim() : undefined,
    papAvailable: papAvailability === 'all' ? undefined : papAvailability,
    otc: otc === 'all' ? undefined : otc,
    manufacturer: manufacturer?.trim() ? manufacturer.trim() : undefined,
    sort: sort || undefined,
  };
};

const handleResponse = <T>(
  response: IResponse<T>,
  options?: { silent?: boolean },
) => {
  if (response.error) {
    if (!options?.silent) {
      showResult(response.error);
    }
    throw new Error(response.error);
  }
  return response.data;
};

const getAdminMedicationsFn = async (filter: TAdminMedicationsFilter = {}) => {
  const params = buildQueryParams(filter);
  const response = await dataService.getList<IAdminMedicationListResponse>(
    apiUrlGenerator(API_ADMIN_MEDICATIONS, params),
  );

  return handleResponse(response);
};

const getAdminMedicationDetailsFn = async (medicationGuid: string) => {
  const response = await dataService.getOne<IAdminMedicationDetails>(
    API_ADMIN_MEDICATIONS,
    medicationGuid,
  );

  return handleResponse(response);
};

const getAdminMedicationPapFormsFn = async (
  medicationGuid: string,
  page = 1,
  limit = 10,
) => {
  const params = { page, limit };
  const response = await dataService.getList<IAdminPapFormsResponse>(
    apiUrlGenerator(apiAdminMedicationPapForms(medicationGuid), params),
  );

  return handleResponse(response);
};

export const getAdminMedicationsKey = (
  filter: TAdminMedicationsFilter = {},
) => ['adminMedications', buildQueryParams(filter)];

export const useAdminMedications = (filter: TAdminMedicationsFilter = {}) => {
  const { data, ...rest } = useQuery({
    queryKey: getAdminMedicationsKey(filter),
    queryFn: async () => getAdminMedicationsFn(filter),
    keepPreviousData: true,
  });

  return { medications: data, ...rest };
};

export const useAdminMedicationDetails = (medicationGuid?: string) => {
  const enabled = Boolean(medicationGuid);
  const { data, ...rest } = useQuery({
    queryKey: ['adminMedicationDetails', medicationGuid],
    queryFn: async () =>
      medicationGuid ? getAdminMedicationDetailsFn(medicationGuid) : null,
    enabled,
  });

  return { medication: data, ...rest };
};

export const useAdminMedicationPapForms = (
  medicationGuid?: string,
  page = 1,
  limit = 10,
) => {
  const { data, ...rest } = useQuery({
    queryKey: ['adminMedicationPapForms', medicationGuid, page, limit],
    queryFn: async () =>
      medicationGuid
        ? getAdminMedicationPapFormsFn(medicationGuid, page, limit)
        : null,
    enabled: Boolean(medicationGuid),
    keepPreviousData: true,
  });

  return { papForms: data, ...rest };
};

export const useAdminMedicationsActions = () => {
  const deleteApi = useApi();
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [downloadingPapFormGuid, setDownloadingPapFormGuid] = useState<
    string | null
  >(null);

  const deleteMedication = async (medicationGuid: string) => {
    await deleteApi.deleteData(API_ADMIN_MEDICATIONS, medicationGuid);
  };

  const createPapForm = async (payload: ICreatePapFormPayload) => {
    try {
      setCreating(true);
      const response = await dataService.createOne(
        apiAdminMedicationPapForms(payload.medicationGroupKey),
        payload,
      );

      return handleResponse(response, { silent: true });
    } finally {
      setCreating(false);
    }
  };
  const uploadPapFormToS3 = async (uploadUrl: string, file: File) => {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/pdf',
        'x-amz-server-side-encryption': 'AES256',
      },
      body: file,
    });

    if (!res.ok) {
      throw new Error('Upload to S3 failed');
    }
  };
  const confirmPapForm = async (guid: string) => {
    try {
      setConfirming(true);
      const response = await dataService.createOne(
        apiAdminPapFormsConfirm(guid),
      );
      return handleResponse(response);
    } finally {
      setConfirming(false);
    }
  };

  const downloadPapForm = async (papFormGuid: string) => {
    try {
      setDownloadingPapFormGuid(papFormGuid);
      const response = await dataService.getOnly<{ downloadUrl?: string }>(
        apiPapFormTemplateDownload(papFormGuid),
      );
      return handleResponse(response);
    } finally {
      setDownloadingPapFormGuid(null);
    }
  };

  const editPapForm = async (
    papFormGuid: string,
    payload: IEditPapFormPayload,
  ) => {
    try {
      setEditing(true);
      const response = await dataService.updateOnly(
        apiAdminPapFormUpdate(papFormGuid),
        payload,
      );
      return handleResponse(response, { silent: true });
    } finally {
      setEditing(false);
    }
  };
  const updatePapFormStatus = async (
    papFormGuid: string,
    payload: IUpdatePapFormStatusPayload,
  ) => {
    try {
      setUpdatingStatus(true);
      const response = await dataService.patchOnly(
        apiAdminPapFormUpdate(papFormGuid),
        payload,
      );
      return handleResponse(response);
    } finally {
      setUpdatingStatus(false);
    }
  };
  return {
    deleteMedication,
    createPapForm,
    uploadPapFormToS3,
    confirmPapForm,
    downloadPapForm,
    editPapForm,
    updatePapFormStatus,
    isDeleting: deleteApi.loading,
    isCreating: creating,
    isConfirming: confirming,
    isEditing: editing,
    isUpdatingStatus: updatingStatus,
    downloadingPapFormGuid,
  };
};

export { useUpdateMedicationDetail } from './use-update-medication-detail';
